import { Provider as RollbarProvider } from '@rollbar/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { DEFAULT_THEME, ThemeProvider } from '@zendeskgarden/react-theming'
import 'react-app-polyfill/ie11'
import { createRoot } from 'react-dom/client'
import { RouterProvider } from 'react-router-dom'
import { css } from 'styled-components'

import { SQLiteDebugger } from './components/SQLiteDebugger'
import { router } from './components/app/AppRouter'
import './index.css'
import './indexV2.css'
import { apiClientSetup } from './modules/apiclient'
import { NotificationsManagerProvider } from './providers/NotificationsManagerProvider'
import { PopoverManagerProvider } from './providers/PopoverManagerProvider'
import { IS_DEVELOPMENT } from './utils/constants'

apiClientSetup()

// Theme is required for many zendesk-garden components (e.g. Tooltip, Dropdown)
// TODO: remove theme provider once back-porting is complete and zendesk-garden is fully replaced
const theme = {
    ...DEFAULT_THEME,
    colors: {
        ...DEFAULT_THEME.colors,
        primaryHue: '#314A68',
    },
    components: {
        'chrome.chrome': css`
            font-family: 'DM Sans', sans-serif;
        `,
        'buttons.button': css`
            border-radius: 8px;
            padding: 8px 16px;
            font-size: 13px;
            font-family: 'DM Sans', sans-serif;
            height: 32px;
        `,
        'dropdowns.item': css`
            color: #314a68;
        `,
        'dropdowns.select': css`
            height: 26px;
            padding-top: 3px;
        `,
        'forms.media_figure': css`
            margin-top: 6px;
        `,
        'forms.input': css`
            border-radius: 4px;
            &::placeholder {
                color: #999999;
            }
        `,
        'forms.input_hint': css`
            color: #999999;
        `,
        'forms.checkbox_hint': css`
            color: #999999;
        `,
        'accordions.panel': css`
            border: none;
        `,
        'accordions.section': css`
            padding-bottom: 12px;
        `,
        'accordions.button': css`
            font-size: 17px;
            font-weight: 700;
            line-height: 22px;
            padding: 0 0 0 24px;
            color: #000000;
        `,
        'accordions.rotate_icon': css`
            padding: 0 8px 0 8px;
        `,
    },
}

const rollbarConfig = {
    enabled: import.meta.env.MODE !== 'development',
    accessToken: import.meta.env.VITE_ROLLBAR_ACCESS_TOKEN,
    captureUncaught: true,
    captureUnhandledRejections: true,
    payload: {
        environment: import.meta.env.MODE,
        client: {
            javascript: {
                source_map_enabled: true,
                code_version: __COMMIT_HASH__,
            },
        },
    },
}

const isBrowserSupported = async (): Promise<boolean> => {
    try {
        if (
            typeof navigator.locks === 'undefined' ||
            typeof window.SharedWorker === 'undefined' ||
            typeof window.BroadcastChannel === 'undefined'
        ) {
            return false
        }
        const _ = await navigator.storage.getDirectory()
        return true
    }
    catch {
        return false
    }
}

const showUnsupportedBrowserMessage = () => {
    document.getElementById('root-spinner')?.remove()
    const unsupportedBrowserMessageElement = document.getElementById('unsupported-browser-message')
    if (unsupportedBrowserMessageElement) {
        unsupportedBrowserMessageElement.style.display = 'flex'
    }
}

const queryClient = new QueryClient({
    defaultOptions: { queries: { staleTime: 1000 * 60 * 15 } },
})

const boot = async () => {
    if (!(await isBrowserSupported())) {
        showUnsupportedBrowserMessage()
        return
    }

    createRoot(document.getElementById('root')!).render(
        <RollbarProvider config={rollbarConfig}>
            <ThemeProvider theme={theme}>
                <PopoverManagerProvider>
                    <NotificationsManagerProvider>
                        <QueryClientProvider client={queryClient}>
                            <RouterProvider router={router} future={{ v7_startTransition: true }} />
                            {IS_DEVELOPMENT && <SQLiteDebugger />}
                        </QueryClientProvider>
                    </NotificationsManagerProvider>
                </PopoverManagerProvider>
            </ThemeProvider>
        </RollbarProvider>
    )
}
boot()
